//App-Level SEO Configuration and Data
// Created: 2025-12-07
// Purpose: Central SEO configuration for EducExpertAI application
// Target: Rank #1 for AI-Powered LMS, Educational Platform, Competitive Exams

import {
  SEOPageData,
  WebsiteStructuredData,
  OrganizationStructuredData,
  StructuredDataBase,
} from '@/components/SEO/types';

// ===== APP CONFIGURATION =====
export const APP_SEO_CONFIG = {
  domain: 'https://www.eduexpertai.com',
  siteName: 'EduExpertAI',
  organizationName: 'EduExpertAI',
  tagline: "India's #1 AI-Powered LMS - Interactive Learning, Skill Development & Exam Preparation",
  description: 'AI-powered educational platform for interactive learning, skill development, competitive exam preparation, current affairs, and job portal - All powered by AI',
  email: 'support@eduexpertai.com',
  phone: '+91-6360501798',
  foundingYear: '2024',
  logo: {
    main: 'https://www.eduexpertai.com/logo.png',
    small: 'https://www.eduexpertai.com/logo.png',
  },
  socialMedia: {
    facebook: 'https://www.facebook.com/EducExpertAI',
    twitter: 'https://twitter.com/EducExpertAI',
    linkedin: 'https://www.linkedin.com/company/eduexpertai',
    youtube: 'https://www.youtube.com/EducExpertAI',
    instagram: 'https://www.instagram.com/eduexpertai',
  },
  twitterHandle: '@EducExpertAI',
};

// ===== HOMEPAGE SEO =====
export const homepageSEO: SEOPageData = {
  meta: {
    title: 'EduExpertAI - AI-Powered LMS | Interactive Learning & Exam Preparation',
    description: "India's #1 AI-powered Learning Management System. Interactive learning, skill development courses, competitive exam preparation (UPSC, SSC, Banking), daily current affairs with audio, AI-powered job portal, and QBot AI tutor. Complete education ecosystem powered by AI. Trusted by 100,000+ students.",
    keywords: [
      // Core Platform
      'AI-powered LMS',
      'EduExpertAI',
      'learning management system India',
      'AI education platform',
      
      // Interactive Learning
      'interactive learning platform',
      'AI-powered interactive learning',
      'interactive online courses',
      'gamified learning',
      'real-time learning',
      'live interactive classes',
      
      // Skill Development
      'skill development courses',
      'AI-powered skill development',
      'professional skill training',
      'upskilling platform',
      'online skill courses India',
      'career development courses',
      
      // Competitive Exam Preparation
      'competitive exam preparation',
      'UPSC preparation online',
      'SSC exam coaching',
      'banking exam preparation',
      'railway exam preparation',
      'government exam preparation',
      'GATE preparation',
      'state PSC preparation',
      'exam preparation app',
      'mock tests AI analytics',
      
      // Current Affairs
      'current affairs 2025',
      'daily current affairs',
      'current affairs for competitive exams',
      'current affairs with audio',
      'AI-powered current affairs',
      
      // Job Portal
      'AI job portal',
      'AI-powered job matching',
      'career opportunities',
      'job search platform',
      'employment portal India',
      
      // AI Features
      'QBot AI tutor',
      'AI-powered education',
      'personalized learning AI',
      'AI career guidance',
      'AI job recommendations',
    ],
    canonical: APP_SEO_CONFIG.domain,
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    author: 'EducExpertAI',
    viewport: 'width=device-width, initial-scale=1.0',
    language: 'en-IN',
  },
  openGraph: {
    title: 'EducExpertAI - AI-Powered LMS | Interactive Learning, Skills & Exam Prep',
    description: "India's most advanced AI-powered learning platform. Interactive learning, skill development, competitive exam preparation (UPSC, SSC, Banking), current affairs, job portal - all powered by AI. QBot AI tutor, 10,000+ mock tests, personalized learning paths.",
    image: `${APP_SEO_CONFIG.domain}/logo.png`,
    imageAlt: 'EducExpertAI - AI-Powered Learning Management System for Interactive Learning & Career Growth',
    url: APP_SEO_CONFIG.domain,
    type: 'website',
    siteName: APP_SEO_CONFIG.siteName,
    locale: 'en_IN',
    imageWidth: '1200',
    imageHeight: '630',
    imageType: 'image/jpeg',
  },
  twitter: {
    card: 'summary_large_image',
    site: APP_SEO_CONFIG.twitterHandle,
    creator: APP_SEO_CONFIG.twitterHandle,
    title: 'EducExpertAI - AI-Powered Interactive Learning & Career Platform',
    description: "Interactive learning, skill development, competitive exams (UPSC, SSC), current affairs, AI job portal. QBot AI tutor, 10,000+ tests. Complete AI-powered education ecosystem.",
    image: `${APP_SEO_CONFIG.domain}/logo.png`,
    imageAlt: 'EducExpertAI - Complete AI-Powered Education Platform',
  },
  structuredData: [],
};

// ===== ORGANIZATION STRUCTURED DATA =====
export const organizationSchema: OrganizationStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: APP_SEO_CONFIG.organizationName,
  alternateName: 'EduExpertAI',
  url: APP_SEO_CONFIG.domain,
  logo: {
    '@type': 'ImageObject',
    url: APP_SEO_CONFIG.logo.main,
    width: '512',
    height: '512',
  },
  description: APP_SEO_CONFIG.description,
  foundingDate: APP_SEO_CONFIG.foundingYear,
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'IN',
    addressRegion: 'Karnataka',
    addressLocality: 'Bangalore',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: APP_SEO_CONFIG.phone,
    email: APP_SEO_CONFIG.email,
    contactType: 'Customer Support',
    availableLanguage: ['English', 'Hindi'],
  },
  sameAs: Object.values(APP_SEO_CONFIG.socialMedia),
};

// ===== WEBSITE STRUCTURED DATA =====
export const websiteSchema: WebsiteStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: APP_SEO_CONFIG.siteName,
  url: APP_SEO_CONFIG.domain,
  description: APP_SEO_CONFIG.description,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${APP_SEO_CONFIG.domain}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
  publisher: {
    '@type': 'Organization',
    name: APP_SEO_CONFIG.organizationName,
    logo: APP_SEO_CONFIG.logo.main,
  },
};

// ===== SOFTWARE APPLICATION SCHEMA (LMS Platform) =====
export const lmsPlatformSchema: StructuredDataBase = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'EducExpertAI - AI-Powered Learning & Career Platform',
  applicationCategory: 'EducationalApplication',
  operatingSystem: 'Web, Android, iOS',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'INR',
    availability: 'https://schema.org/InStock',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: '10000',
    bestRating: '5',
    worstRating: '1',
  },
  description: "India's most comprehensive AI-powered education platform. Interactive learning with live sessions, skill development courses, competitive exam preparation (UPSC, SSC, Banking, Railway), daily current affairs with AI analysis and audio, AI-powered job portal with smart matching, and QBot AI tutor for 24/7 support.",
  featureList: [
    'Interactive Learning - Live classes with real-time engagement',
    'Skill Development - 500+ professional courses with certifications',
    'Competitive Exam Prep - UPSC, SSC, Banking, Railway, GATE, State PSC',
    'QBot AI Tutor - 24/7 intelligent doubt solving and study guidance',
    '10,000+ Mock Tests - AI-powered performance analytics',
    'Current Affairs - Daily updates with audio summaries and exam focus',
    'AI Job Portal - Smart job matching and career recommendations',
    'Personalized Learning Paths - AI-driven course recommendations',
    'Performance Analytics - Rank prediction and progress tracking',
    'Live Interactive Sessions - Real-time polls, quizzes, and engagement',
  ],
};

// ===== ROUTE-SPECIFIC SEO =====
export const routeSEOData: Record<string, Partial<SEOPageData>> = {
  '/': homepageSEO,
  
  '/courses': {
    meta: {
      title: 'Online Courses - UPSC, SSC, Banking, Railway | EducExpertAI',
      description: 'Choose from 500+ courses for competitive exams. Expert-curated content, AI-powered personalization, live classes, doubt clearing with QBot. Start your success journey today.',
      keywords: [
        'online courses India',
        'competitive exam courses',
        'UPSC courses',
        'SSC courses',
        'banking courses',
        'railway courses',
        'exam preparation courses',
        'best online courses',
      ],
      canonical: `${APP_SEO_CONFIG.domain}/courses`,
    },
    openGraph: {
      title: 'Online Courses for Competitive Exams | EducExpertAI',
      description: '500+ expert-curated courses for UPSC, SSC, Banking, Railway exams with AI-powered learning.',
      image: `${APP_SEO_CONFIG.domain}/logo.png`,
      url: `${APP_SEO_CONFIG.domain}/courses`,
      type: 'website',
      siteName: APP_SEO_CONFIG.siteName,
    },
  },
  
  '/qbot': {
    meta: {
      title: 'QBot - AI Tutor for Competitive Exams | 24/7 Doubt Solving | EducExpertAI',
      description: 'Meet QBot, your personal AI tutor. Get instant answers, personalized study plans, practice questions, and exam tips 24/7. Learn smarter, not harder with AI-powered education.',
      keywords: [
        'QBot',
        'AI tutor',
        'AI learning assistant',
        'doubt solving AI',
        'competitive exam AI tutor',
        'online AI tutor',
        'AI-powered education',
        '24/7 tutor',
      ],
      canonical: `${APP_SEO_CONFIG.domain}/qbot`,
    },
    openGraph: {
      title: 'QBot - Your 24/7 AI Tutor for Competitive Exams',
      description: 'Instant doubt solving, personalized study plans, and exam tips powered by AI. Available 24/7.',
      image: `${APP_SEO_CONFIG.domain}/og-qbot.jpg`,
      url: `${APP_SEO_CONFIG.domain}/qbot`,
      type: 'website',
      siteName: APP_SEO_CONFIG.siteName,
    },
  },
  
  '/tests': {
    meta: {
      title: 'Mock Tests & Test Series - UPSC, SSC, Banking | AI-Powered Analytics | EducExpertAI',
      description: '10,000+ mock tests with AI-powered performance analytics. Real exam simulation, detailed solutions, rank prediction. Practice for UPSC, SSC, Banking, Railway exams.',
      keywords: [
        'mock tests',
        'test series',
        'online tests',
        'UPSC mock test',
        'SSC test series',
        'banking mock test',
        'railway test series',
        'practice tests',
        'AI analytics',
      ],
      canonical: `${APP_SEO_CONFIG.domain}/tests`,
    },
    openGraph: {
      title: 'Mock Tests & Test Series with AI Analytics | EducExpertAI',
      description: '10,000+ mock tests for UPSC, SSC, Banking, Railway exams with detailed analytics and rank prediction.',
      image: `${APP_SEO_CONFIG.domain}/logo.png`,
      url: `${APP_SEO_CONFIG.domain}/tests`,
      type: 'website',
      siteName: APP_SEO_CONFIG.siteName,
    },
  },
  
  '/current-affairs': {
    // Already has complete SEO from CurrentAffairsSEOManager
    meta: {
      title: 'Current Affairs 2025 | UPSC, SSC, Banking & Railway Exam Preparation | EducExpertAI',
      description: 'Daily current affairs 2025 for UPSC, SSC, Banking, Railway exams. 500+ articles with detailed analysis, exam questions, PDF downloads. Updated hourly with AI-powered insights and audio summaries.',
      keywords: ['current affairs 2025', 'UPSC current affairs', 'SSC current affairs', 'banking current affairs'],
      canonical: `${APP_SEO_CONFIG.domain}/current-affairs`,
    },
  },
  
  '/interactive-learning': {
    meta: {
      title: 'Interactive Learning Platform | AI-Powered Live Classes & Real-Time Engagement | EducExpertAI',
      description: 'Experience next-generation interactive learning with AI-powered live classes, real-time polls & quizzes, gamified sessions, and instant feedback. Engage with expert instructors and peers in an immersive learning environment.',
      keywords: [
        'interactive learning',
        'AI-powered interactive classes',
        'live interactive sessions',
        'real-time learning',
        'gamified learning',
        'interactive online courses',
        'virtual classroom',
        'live polls and quizzes',
        'student engagement platform',
      ],
      canonical: `${APP_SEO_CONFIG.domain}/interactive-learning`,
    },
    openGraph: {
      title: 'Interactive Learning Platform with AI | EducExpertAI',
      description: 'AI-powered interactive learning with live classes, real-time engagement, and gamification. Learn with instant feedback and peer collaboration.',
      image: `${APP_SEO_CONFIG.domain}/logo.png`,
      url: `${APP_SEO_CONFIG.domain}/interactive-learning`,
      type: 'website',
      siteName: APP_SEO_CONFIG.siteName,
    },
  },
  
  '/skill-development': {
    meta: {
      title: 'Skill Development Courses | Professional Training & Certifications | EducExpertAI',
      description: '500+ AI-powered skill development courses for career growth. Learn in-demand skills - programming, data science, digital marketing, soft skills, and more. Industry-recognized certifications and placement support.',
      keywords: [
        'skill development courses',
        'professional skill training',
        'career development',
        'upskilling platform',
        'reskilling courses',
        'online certification courses',
        'job-ready skills',
        'AI-powered skill development',
        'career advancement courses',
        'technical skills training',
      ],
      canonical: `${APP_SEO_CONFIG.domain}/skill-development`,
    },
    openGraph: {
      title: 'Skill Development Courses with AI Guidance | EducExpertAI',
      description: '500+ courses for professional growth. Learn in-demand skills with AI-powered personalization and industry certifications.',
      image: `${APP_SEO_CONFIG.domain}/logo.png`,
      url: `${APP_SEO_CONFIG.domain}/skill-development`,
      type: 'website',
      siteName: APP_SEO_CONFIG.siteName,
    },
  },
  
  '/job-portal': {
    meta: {
      title: 'AI Job Portal | Smart Job Matching & Career Opportunities | EducExpertAI',
      description: 'Find your perfect job with AI-powered job matching. Browse thousands of opportunities, get personalized recommendations, resume building, interview prep, and career guidance. Connect learning with employment.',
      keywords: [
        'AI job portal',
        'AI-powered job matching',
        'job search platform',
        'career opportunities India',
        'smart job recommendations',
        'employment portal',
        'job placement platform',
        'AI career guidance',
        'resume builder AI',
        'interview preparation',
      ],
      canonical: `${APP_SEO_CONFIG.domain}/job-portal`,
    },
    openGraph: {
      title: 'AI-Powered Job Portal | Smart Career Matching | EducExpertAI',
      description: 'Find jobs that match your skills with AI. Thousands of opportunities, personalized recommendations, and career guidance.',
      image: `${APP_SEO_CONFIG.domain}/logo.png`,
      url: `${APP_SEO_CONFIG.domain}/job-portal`,
      type: 'website',
      siteName: APP_SEO_CONFIG.siteName,
    },
  },

  '/ourteam': {
    meta: {
      title: 'Our Team | Leadership at EduExpertAI',
      description:
        'Meet the leadership behind EduExpertAI. Srinivas PC, Co-Founder and Director, has mentored 500+ civil service aspirants across two and a half decades; Dr. Kalyankumar Hatti, Co-Founder and Director, brings 25+ years of engineering and skill development leadership.',
      keywords: [
        'EduExpertAI team',
        'EduExpertAI leadership',
        'EduExpertAI founders',
        'Srinivas PC',
        'Srinivas PC EduExpertAI',
        'Kalyankumar Hatti',
        'Dr Kalyankumar Hatti EduExpertAI',
        'co-founder and director EduExpertAI',
        'civil services mentor Bengaluru',
        'UPSC mentor Karnataka',
        'skill development leadership India',
        'about EduExpertAI',
      ],
      canonical: `${APP_SEO_CONFIG.domain}/ourteam`,
    },
    openGraph: {
      title: 'Our Team | Leadership at EduExpertAI',
      description:
        'The educators and mentors behind EduExpertAI - decades of experience guiding learners through competitive examinations.',
      image: `${APP_SEO_CONFIG.domain}/team/srinivas-pc.webp`,
      imageAlt: 'Srinivas PC, Co-Founder and Director of EduExpertAI',
      url: `${APP_SEO_CONFIG.domain}/ourteam`,
      type: 'profile',
      siteName: APP_SEO_CONFIG.siteName,
    },
    twitter: {
      card: 'summary_large_image',
      site: APP_SEO_CONFIG.twitterHandle,
      title: 'Our Team | Leadership at EduExpertAI',
      description:
        'Meet the leadership behind EduExpertAI - Co-Founders and Directors Srinivas PC and Dr. Kalyankumar Hatti.',
      image: `${APP_SEO_CONFIG.domain}/team/srinivas-pc.webp`,
      imageAlt: 'Srinivas PC, Co-Founder and Director of EduExpertAI',
    },
    // The first portrait is the page's largest contentful paint. Preloading it
    // here starts the download while the lazily-imported page chunk is still
    // in flight; the path must stay identical to the `photo` in the team data.
    preloadImages: ['/team/srinivas-pc.webp'],
    // Person markup is what earns the knowledge-panel style result for a
    // leadership page, so this route replaces the default schema trio with an
    // ItemList of Person nodes and keeps the Organization schema alongside it.
    // Each person's `url` is the anchor its card carries on the page.
    structuredData: [
      organizationSchema,
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Our Team | EduExpertAI',
        url: `${APP_SEO_CONFIG.domain}/ourteam`,
        description: 'The leadership team behind EduExpertAI.',
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@type': 'Person',
                name: 'Srinivas PC',
                jobTitle: 'Co-Founder and Director',
                image: `${APP_SEO_CONFIG.domain}/team/srinivas-pc.webp`,
                url: `${APP_SEO_CONFIG.domain}/ourteam#srinivas-pc`,
                description:
                  'Co-Founder and Director of EduExpertAI. 21 years of service at the President’s House, former Convenor of the Kannada Adhyayana Peeta at JNU Delhi, and mentor to over 500 bureaucrats across two and a half decades.',
                worksFor: {
                  '@type': 'Organization',
                  name: APP_SEO_CONFIG.organizationName,
                  url: APP_SEO_CONFIG.domain,
                },
                homeLocation: {
                  '@type': 'Place',
                  address: {
                    '@type': 'PostalAddress',
                    addressLocality: 'Bengaluru',
                    addressRegion: 'Karnataka',
                    addressCountry: 'IN',
                  },
                },
                knowsAbout: [
                  'Civil Services Mentoring',
                  'Governance',
                  'Institution Building',
                  'Community Outreach',
                  'Scholarships',
                ],
              },
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@type': 'Person',
                name: 'Dr. Kalyankumar Hatti',
                jobTitle: 'Co-Founder and Director',
                url: `${APP_SEO_CONFIG.domain}/ourteam#kalyankumar-hatti`,
                description:
                  'Co-Founder and Director of EduExpertAI. Founder Chairman of the Metamorphosis Engineering & Skill Group and formerly Global Head of the Strategic CV Portfolio at Ashok Leyland.',
                sameAs: ['https://www.linkedin.com/in/dr-kalyankumar-hatti-43a12885/'],
                worksFor: {
                  '@type': 'Organization',
                  name: APP_SEO_CONFIG.organizationName,
                  url: APP_SEO_CONFIG.domain,
                },
                alumniOf: [
                  { '@type': 'CollegeOrUniversity', name: 'Indian Institute of Management Bangalore' },
                  { '@type': 'CollegeOrUniversity', name: 'Karnatak University' },
                ],
                homeLocation: {
                  '@type': 'Place',
                  address: {
                    '@type': 'PostalAddress',
                    addressLocality: 'Chennai',
                    addressRegion: 'Tamil Nadu',
                    addressCountry: 'IN',
                  },
                },
                knowsAbout: [
                  'Skill Development',
                  'Engineering Education',
                  'Industry Partnerships',
                  'Mobility and Electric Vehicles',
                  'Strategy',
                ],
              },
            },
          ],
        },
      },
    ],
  },
};

// ===== GET SEO DATA BY ROUTE =====
export const getSEOByRoute = (pathname: string): SEOPageData => {
  const routeData = routeSEOData[pathname];
  
  if (!routeData) {
    return homepageSEO;
  }
  
  return {
    ...homepageSEO,
    ...routeData,
    meta: { ...homepageSEO.meta, ...routeData.meta },
    openGraph: { ...homepageSEO.openGraph, ...routeData.openGraph },
    twitter: { ...homepageSEO.twitter, ...routeData.twitter },
    structuredData: routeData.structuredData || [
      websiteSchema,
      organizationSchema,
      lmsPlatformSchema,
    ],
  } as SEOPageData;
};

// ===== APP-WIDE STRUCTURED DATA =====
export const appStructuredData: StructuredDataBase[] = [
  websiteSchema,
  organizationSchema,
  lmsPlatformSchema,
];
